import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
    ComposedChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
    Area,
} from 'recharts';
import { Card, Box, Typography, Chip, Grid, LinearProgress, IconButton, useTheme, useMediaQuery, List, ListItem, ListItemAvatar, Avatar, ListItemText, Divider } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import ScienceIcon from '@mui/icons-material/Science';
import BugReportIcon from '@mui/icons-material/BugReport';
import GrassIcon from '@mui/icons-material/Grass';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import EventUsageSummary from './EventUsageSummary';

const SECONDARY = '#F9AA33';

// Event type colors
const eventTypeColors = {
    fertilizer: '#82ca9d',
    fungicide: '#8884d8',
    insecticide: '#ffc658',
    herbicide: '#d32f2f',
    harvest: '#4caf50',
    sale: '#ff9800',
    default: '#bdbdbd',
};

// Generate date range array
const generateDateRange = (startDate, endDate) => {
    const dates = [];
    let currentDate = new Date(startDate);
    const lastDate = new Date(endDate);

    while (currentDate <= lastDate) {
        dates.push(currentDate.toISOString().split('T')[0]);
        currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
};

// Custom Weather Tooltip
const WeatherTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
            <Box sx={{ 
                background: '#181F2A', 
                padding: '10px', 
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 1,
                color: '#fff'
            }}>
                <Typography variant="body2"><strong>Date:</strong> {label}</Typography>
                <Typography variant="body2"><strong>Max Temp:</strong> {data.temp_max}°C</Typography>
                <Typography variant="body2"><strong>Min Temp:</strong> {data.temp_min}°C</Typography>
                <Typography variant="body2"><strong>Precipitation:</strong> {data.precipitation} mm</Typography>
            </Box>
        );
    }
    return null;
};

// Custom Event Tooltip
const EventTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        const details = data.details || {};
        return (
            <Box sx={{ 
                background: '#181F2A', 
                padding: '10px', 
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 1,
                color: '#fff'
            }}>
                <Typography variant="body2"><strong>Date:</strong> {data.date}</Typography>
                <Typography variant="body2"><strong>Category:</strong> {data.category}</Typography>
                {details.dat !== undefined && (
                    <Typography variant="body2"><strong>DAT:</strong> {details.dat}</Typography>
                )}
                {/* Combine amount and unit fields for relevant keys */}
                {Object.entries(details).map(([key, value]) => {
                    if (key === 'dat') return null;
                    // Sale amount
                    if (key === 'sale_amt' && details.sale_unit) {
                        return (
                            <Typography key={key} variant="body2">
                                <strong>Sale Amt:</strong> {details.sale_unit} {value}
                            </Typography>
                        );
                    }
                    // Harvest amount
                    if (key === 'harvest_amt' && details.harvest_unit) {
                        return (
                            <Typography key={key} variant="body2">
                                <strong>Harvest Amt:</strong> {details.harvest_unit} {value}
                            </Typography>
                        );
                    }
                    // Amount used (for input events)
                    if (key === 'amt_used' && details.unit) {
                        return (
                            <Typography key={key} variant="body2">
                                <strong>Amt Used:</strong> {details.unit} {value}
                            </Typography>
                        );
                    }
                    // If the key is a unit, skip (already shown)
                    if (key === 'unit' || key === 'sale_unit' || key === 'harvest_unit') return null;
                    // Default: show as is
                    return (
                        <Typography key={key} variant="body2">
                            <strong>{key.replace('_', ' ')}:</strong> {value}
                        </Typography>
                    );
                })}
            </Box>
        );
    }
    return null;
};

// Helper for fallback chart message
const ChartFallback = ({ message }) => (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F9AA33', fontWeight: 600, fontSize: 18, background: 'rgba(255,255,255,0.02)', borderRadius: 2 }}>
        {message}
    </Box>
);

// Placeholder FarmDetailsCard styled like the provided image, but vertical
const FarmDetailsCard = ({ crop, onBack }) => {
    if (!crop) return null;
    const progress = crop.target_yield > 0 ? Math.round((crop.actual_yield / crop.target_yield) * 100) : 0;
    const unit =  crop.target_yield_unit_type || crop.yield_unit || '';
    const total_yield_unit = crop.yield_unit || 'kg';
    return (
        <Card sx={{
            width: '100%',
            height: '100%',
            backgroundColor: '#181F2A',
            color: '#fff',
            borderRadius: 2,
            boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)',
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            overflow: 'auto',
        }}>
            {/* Header: Back button and title */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1, ml: -0.5 }}>
                <IconButton onClick={onBack} sx={{
                    color: '#F9AA33',
                    background: alpha('#181F2A', 0.12),
                    borderRadius: '50%',
                    fontWeight: 600,
                    mr: 1,
                    transition: 'background 0.2s',
                    '&:hover': {
                        background: alpha('#181F2A', 0.18),
                    },
                }} size="small">
                    <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#fff', letterSpacing: 0.2, lineHeight: 1.1, pt: 1.5}}>
                        {crop.variety_name}
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#AAB4C8', fontWeight: 500, lineHeight: 1.1, pt: .5}}>
                        {crop.plant_type}
                    </Typography>
                </Box>
            </Box>
            {/* Data grid: 2 columns, 5 rows */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 1, columnGap: 3, ml: 4.5 }}>
                {/* Row 1 */}
                <Box>
                    <Typography variant="caption" sx={{ color: '#AAB4C8', fontWeight: 500 }}>Section</Typography>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, lineHeight: 1.1, whiteSpace: 'nowrap' }}>{crop.zone_name}</Typography>
                </Box>
                <Box>
                    <Typography variant="caption" sx={{ color: '#AAB4C8', fontWeight: 500 }}>Plot</Typography>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, lineHeight: 1.1, whiteSpace: 'nowrap' }}>{crop.plot_name}</Typography>
                </Box>
                {/* Row 2 */}
                <Box>
                    <Typography variant="caption" sx={{ color: '#AAB4C8', fontWeight: 500 }}>Plot Area</Typography>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, lineHeight: 1.1, whiteSpace: 'nowrap' }}>{crop.plot_area}</Typography>
                </Box>
                <Box>
                    <Typography variant="caption" sx={{ color: '#AAB4C8', fontWeight: 500 }}>Planting Date</Typography>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, lineHeight: 1.1, whiteSpace: 'nowrap' }}>{crop.planting_date}</Typography>
                </Box>
                {/* Row 3 */}
                <Box>
                    <Typography variant="caption" sx={{ color: '#AAB4C8', fontWeight: 500 }}>Transplant Date</Typography>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, lineHeight: 1.1, whiteSpace: 'nowrap' }}>{crop.transplant_date}</Typography>
                </Box>
                <Box>
                    <Typography variant="caption" sx={{ color: '#AAB4C8', fontWeight: 500 }}>Last Harvest Date</Typography>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, lineHeight: 1.1, whiteSpace: 'nowrap' }}>{crop.last_harvest_date}</Typography>
                </Box>
                {/* Row 4 */}
                <Box>
                    <Typography variant="caption" sx={{ color: '#AAB4C8', fontWeight: 500 }}>Units Planted</Typography>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, lineHeight: 1.1, whiteSpace: 'nowrap' }}>{crop.units_planted}</Typography>
                </Box>
                <Box>
                    <Typography variant="caption" sx={{ color: '#AAB4C8', fontWeight: 500 }}>Total Yield</Typography>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, lineHeight: 1.1, whiteSpace: 'nowrap' }}>{crop.total_yield} {total_yield_unit}</Typography>
                </Box>
                {/* Row 5 */}
                <Box>
                    <Typography variant="caption" sx={{ color: '#AAB4C8', fontWeight: 500 }}>Total Sales</Typography>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, lineHeight: 1.1, whiteSpace: 'nowrap' }}>{crop.total_sales}</Typography>
                </Box>
                <Box>
                    <Typography variant="caption" sx={{ color: '#AAB4C8', fontWeight: 500 }}>Duration</Typography>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, lineHeight: 1.1, whiteSpace: 'nowrap' }}>{crop.duration ? `${crop.duration} days` : ''}</Typography>
                </Box>
                <Box />
            </Box>
            {/* Progress bar with actual/target labels */}
            <Box sx={{ pt: 1, ml: 0.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#AAB4C8', fontWeight: 500 }}>Actual vs Target Yield</Typography>
                    <Typography variant="caption" sx={{ color: '#fff', fontWeight: 500 }}>{crop.actual_yield} {unit}  of  {crop.target_yield} {unit}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress variant="determinate" value={progress} sx={{ flex: 1, height: 10, borderRadius: 5, background: '#232632', '& .MuiLinearProgress-bar': { background: '#6EE7B7' } }} />
                    <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600, minWidth: 38, ml: 1 }}>{progress}%</Typography>
                </Box>
            </Box>
        </Card>
    );
};

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: '#fff',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    fontSize: 14,
    [theme.breakpoints.up('sm')]: {
      width: '20ch',
      '&:focus': {
        width: '25ch',
      },
    },
  },
}));

// Mobile Search Components
const MobileSearch = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha('#222C2F', 0.8),
  '&:hover': {
    backgroundColor: alpha('#222C2F', 0.9),
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: 0,
    width: 'auto',
  },
}));

const MobileSearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const MobileStyledInputBase = styled(InputBase)(({ theme }) => ({
  color: '#fff',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    fontSize: 14,
    [theme.breakpoints.up('sm')]: {
      width: '20ch',
      '&:focus': {
        width: '25ch',
      },
    },
  },
}));

// Mobile Timeline Events Component
const MobileTimelineEvents = ({ events, startDate, endDate, units_planted }) => {
    const [searchQuery, setSearchQuery] = useState('');
    
    const allEvents = [];
    
    // Process all event types
    ['fertilizerEvents', 'fungicideEvents', 'insecticideEvents', 'herbicideEvents'].forEach((eventTypeKey) => {
        (events[eventTypeKey] || []).forEach((record) => {
            const eventType = eventTypeKey.replace('Events', '');
            allEvents.push({
                ...record,
                _type: eventType,
                _date: record.date,
                _description: `${eventType.charAt(0).toUpperCase() + eventType.slice(1)}: ${record[`${eventType}_name`] || 'N/A'} - ${record.amt_used || 0} ${record.amt_used_unit_type || ''}`,
            });
        });
    });

    (events.harvests || []).forEach((record) => {
        allEvents.push({
            ...record,
            _type: 'harvest',
            _date: record.date,
            _description: `Harvest: ${record.harvest_amt || 0} ${record.unit_type || ''}`,
        });
    });

    (events.sales || []).forEach((record) => {
        allEvents.push({
            ...record,
            _type: 'sale',
            _date: record.date,
            _description: `Sale: ${record.sale_amt || 0} ${record.sale_unit_type || ''} @ ₦${record.unit_price || 0}`,
        });
    });

    // Sort by date (newest first)
    allEvents.sort((a, b) => new Date(b._date) - new Date(a._date));

    // Filter events based on search query
    const filteredEvents = useMemo(() => {
        if (!searchQuery.trim()) return allEvents;
        
        const query = searchQuery.toLowerCase();
        return allEvents.filter(event => 
            event._type.toLowerCase().includes(query) ||
            event._description.toLowerCase().includes(query) ||
            event._date.toLowerCase().includes(query)
        );
    }, [allEvents, searchQuery]);

    const iconMap = {
        fertilizer: <LocalFloristIcon />,
        fungicide: <ScienceIcon />,
        insecticide: <BugReportIcon />,
        herbicide: <GrassIcon />,
        harvest: <AgricultureIcon />,
        sale: <MonetizationOnIcon />,
    };

    const colorMap = {
        fertilizer: '#82ca9d',
        fungicide: '#8884d8',
        insecticide: '#ffc658',
        herbicide: '#d32f2f',
        harvest: '#4caf50',
        sale: '#ff9800',
    };

    return (
        <Box sx={{ flex: 1, minHeight: 0, height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* Search Bar - Sticky */}
            <Box sx={{ 
                position: 'sticky',
                top: 0,
                zIndex: 1,
                bgcolor: 'transparent',
                pt: 1,
                pb: 2
            }}>
                <Box sx={{ width: '100%' }} onClick={(e) => e.stopPropagation()}>
                    <MobileSearch>
                        <MobileSearchIconWrapper>
                            <SearchIcon sx={{ color: '#AAB4C8', fontSize: '18px' }} />
                        </MobileSearchIconWrapper>
                        <MobileStyledInputBase
                            placeholder="Search events..."
                            inputProps={{ 'aria-label': 'search' }}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => e.stopPropagation()}
                        />
                    </MobileSearch>
                </Box>
            </Box>
            
            {/* Events List - Scrollable */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                {filteredEvents.length === 0 ? (
                    <Typography sx={{ color: '#AAB4C8', fontSize: 14, textAlign: 'center', mt: 2 }}>
                        {searchQuery ? 'No events match your search.' : 'No events found for this crop.'}
                    </Typography>
                ) : (
                    <List sx={{ width: '100%', bgcolor: 'transparent', p: 0 }}>
                        {filteredEvents.map((event, idx) => (
                            <React.Fragment key={idx}>
                                <ListItem alignItems="flex-start" sx={{ px: 0, py: 1 }}>
                                    <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: colorMap[event._type] || '#bdbdbd', color: '#181F2A' }}>
                                            {iconMap[event._type]}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={
                                            <Typography sx={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>
                                                {event._type.charAt(0).toUpperCase() + event._type.slice(1)}
                                            </Typography>
                                        }
                                        secondary={
                                            <>
                                                <Typography sx={{ color: '#AAB4C8', fontSize: 13, display: 'inline' }}>
                                                    {event._description}
                                                </Typography>
                                                <Typography sx={{ color: '#AAB4C8', fontSize: 12, ml: 1, display: 'inline' }}>
                                                    {new Date(event._date).toLocaleDateString()}
                                                </Typography>
                                            </>
                                        }
                                        sx={{ width: '100%', minWidth: 0 }}
                                    />
                                </ListItem>
                                {idx < filteredEvents.length - 1 && <Divider variant="inset" component="li" sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />}
                            </React.Fragment>
                        ))}
                    </List>
                )}
            </Box>
        </Box>
    );
};

const CropTimeline = ({ startDate, endDate, latitude, longitude, events, units_planted, cropDetails, onBack }) => {
    const [weatherData, setWeatherData] = useState([]);
    const [prevWeatherData, setPrevWeatherData] = useState([]);
    const [eventData, setEventData] = useState([]);
    const [error, setError] = useState(null);
    const [weatherError, setWeatherError] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Calculate previous year window
    let prevStart, prevEnd;
    if (startDate && endDate) {
        prevStart = new Date(startDate);
        prevEnd = new Date(endDate);
        prevStart.setFullYear(prevStart.getFullYear() - 1);
        prevEnd.setFullYear(prevEnd.getFullYear() - 1);
    }

    // Calculate precipitation stats
    let totalPrecip = 0;
    let prevTotalPrecip = 0;
    let percentChange = 0;
    let isIncrease = true;
    // ET stats
    let totalET = 0;
    let prevTotalET = 0;
    let etPercentChange = 0;
    let etIsIncrease = true;
    if (weatherData.length) {
        totalPrecip = weatherData.reduce((sum, d) => sum + (d.precipitation || 0), 0);
        totalET = weatherData.reduce((sum, d) => sum + (d.et || 0), 0);
    }
    if (prevWeatherData.length) {
        prevTotalPrecip = prevWeatherData.reduce((sum, d) => sum + (d.precipitation || 0), 0);
        prevTotalET = prevWeatherData.reduce((sum, d) => sum + (d.et || 0), 0);
    }
    if (prevWeatherData.length) {
        if (prevTotalPrecip === 0) {
            percentChange = totalPrecip === 0 ? 0 : 100;
            isIncrease = true;
        } else {
            percentChange = ((totalPrecip - prevTotalPrecip) / Math.abs(prevTotalPrecip)) * 100;
            isIncrease = totalPrecip >= prevTotalPrecip;
        }
        if (prevTotalET === 0) {
            etPercentChange = totalET === 0 ? 0 : 100;
            etIsIncrease = true;
        } else {
            etPercentChange = ((totalET - prevTotalET) / Math.abs(prevTotalET)) * 100;
            etIsIncrease = totalET >= prevTotalET;
        }
    }

    // Avg max/min temp and percent change
    let avgMaxTemp = 0;
    let avgMinTemp = 0;
    let prevAvgMaxTemp = 0;
    let prevAvgMinTemp = 0;
    let maxTempPercentChange = 0;
    let minTempPercentChange = 0;
    let maxTempIsIncrease = true;
    let minTempIsIncrease = true;
    if (weatherData.length) {
        avgMaxTemp = weatherData.reduce((sum, d) => sum + (d.temp_max || 0), 0) / weatherData.length;
        avgMinTemp = weatherData.reduce((sum, d) => sum + (d.temp_min || 0), 0) / weatherData.length;
    }
    if (prevWeatherData.length) {
        prevAvgMaxTemp = prevWeatherData.reduce((sum, d) => sum + (d.temp_max || 0), 0) / prevWeatherData.length;
        prevAvgMinTemp = prevWeatherData.reduce((sum, d) => sum + (d.temp_min || 0), 0) / prevWeatherData.length;
    }
    if (prevWeatherData.length) {
        if (prevAvgMaxTemp === 0) {
            maxTempPercentChange = avgMaxTemp === 0 ? 0 : 100;
            maxTempIsIncrease = true;
        } else {
            maxTempPercentChange = ((avgMaxTemp - prevAvgMaxTemp) / Math.abs(prevAvgMaxTemp)) * 100;
            maxTempIsIncrease = avgMaxTemp >= prevAvgMaxTemp;
        }
        if (prevAvgMinTemp === 0) {
            minTempPercentChange = avgMinTemp === 0 ? 0 : 100;
            minTempIsIncrease = true;
        } else {
            minTempPercentChange = ((avgMinTemp - prevAvgMinTemp) / Math.abs(prevAvgMinTemp)) * 100;
            minTempIsIncrease = avgMinTemp >= prevAvgMinTemp;
        }
    }

    // Fetch Weather Data (current and previous year)
    useEffect(() => {
        const fetchWeatherData = async () => {
            try {
                setWeatherData([]);
                setPrevWeatherData([]);
                setError(null);
    
                if (!startDate || !endDate || !latitude || !longitude) {
                    throw new Error('Missing required parameters for fetching weather data');
                }
    
                // Current year
                const response = await axios.get('https://archive-api.open-meteo.com/v1/archive', {
                    params: {
                        latitude,
                        longitude,
                        start_date: startDate,
                        end_date: endDate,
                        daily: 'precipitation_sum,temperature_2m_min,temperature_2m_max,et0_fao_evapotranspiration',
                        timezone: 'auto',
                    },
                });
                const { daily } = response.data;
                if (!daily || !daily.time) {
                    throw new Error('Invalid historical weather data response');
                }
                const combinedWeather = daily.time.map((date, index) => ({
                    date,
                    precipitation: daily.precipitation_sum[index] || 0,
                    temp_min: daily.temperature_2m_min[index] || 0,
                    temp_max: daily.temperature_2m_max[index] || 0,
                    et: daily.et0_fao_evapotranspiration ? daily.et0_fao_evapotranspiration[index] || 0 : 0,
                }));
                setWeatherData(combinedWeather);

                // Previous year
                if (prevStart && prevEnd) {
                    const prevResponse = await axios.get('https://archive-api.open-meteo.com/v1/archive', {
                        params: {
                            latitude,
                            longitude,
                            start_date: prevStart.toISOString().split('T')[0],
                            end_date: prevEnd.toISOString().split('T')[0],
                            daily: 'precipitation_sum,temperature_2m_min,temperature_2m_max,et0_fao_evapotranspiration',
                            timezone: 'auto',
                        },
                    });
                    const { daily: prevDaily } = prevResponse.data;
                    if (prevDaily && prevDaily.time) {
                        const prevWeather = prevDaily.time.map((date, index) => ({
                            date,
                            precipitation: prevDaily.precipitation_sum[index] || 0,
                            temp_min: prevDaily.temperature_2m_min[index] || 0,
                            temp_max: prevDaily.temperature_2m_max[index] || 0,
                            et: prevDaily.et0_fao_evapotranspiration ? prevDaily.et0_fao_evapotranspiration[index] || 0 : 0,
                        }));
                        setPrevWeatherData(prevWeather);
                    }
                }
            } catch (error) {
                setError(error.message);
            }
        };

        const prepareEventData = () => {
            const dateRange = generateDateRange(startDate, endDate);
            const combinedData = [];

            dateRange.forEach((date) => {
                ['fertilizerEvents', 'fungicideEvents', 'insecticideEvents', 'herbicideEvents'].forEach((key) => {
                    (events[key] || []).forEach((record) => {
                        if (record.date === date) {
                            const eventType = key.replace('Events', '');
                            const dat = Math.ceil((new Date(record.date) - new Date(startDate)) / (1000 * 60 * 60 * 24));

                            combinedData.push({
                                date,
                                category: eventType,
                                details: {
                                    date: record.date,
                                    product_name: record[`${eventType}_name`] || 'N/A', // Extract event-specific name
                                    amt_used: record.amt_used || 0,
                                    unit: record.amt_used_unit_type || '',
                                    per_plant: (record.amt_used / units_planted).toFixed(4) || 0,
                                    dat: dat >= 0 ? dat : 'N/A',
                                },
                            });
                        }
                    });
                });

                (events.harvests || []).forEach((record) => {
                    if (record.date === date) {
                        const dat = Math.ceil((new Date(record.date) - new Date(startDate)) / (1000 * 60 * 60 * 24));
                        combinedData.push({
                            date,
                            category: 'harvest',
                            details: {
                                date: record.date,
                                amt: record.harvest_amt || 0,
                                unit: record.unit_type || '',
                                dat: dat >= 0 ? dat : 'N/A',
                            },
                        });
                    }
                });

                (events.sales || []).forEach((record) => {
                    if (record.date === date) {
                        const dat = Math.ceil((new Date(record.date) - new Date(startDate)) / (1000 * 60 * 60 * 24));
                        combinedData.push({
                            date,
                            category: 'sale',
                            details: {
                                date: record.date,
                                harvest_amt: record.harvest_amt || 0,
                                harvest_unit: record.harvest_unit_type || 0,
                                sale_amt: record.sale_amt || 0,
                                sale_unit: record.sale_unit_type || '',
                                unit_price: record.unit_price || 0,
                                dat: dat >= 0 ? dat : 'N/A',
                            },
                        });
                    }
                });
            });

            setEventData(combinedData);
        };

        fetchWeatherData();
        prepareEventData();
    }, [startDate, endDate, latitude, longitude, events]);

    if (error) {
        return (
            <Card sx={{ 
                backgroundColor: '#181F2A', 
                borderRadius: 1.5, 
                boxShadow: '0 2px 8px 0 rgba(0,0,0,0.20)', 
                p: 3,
                mb: 2
            }}>
                <Typography variant="body1" sx={{ color: '#ff6b6b' }}>
                    Error: {error}
                </Typography>
            </Card>
        );
    }
    
    if (!weatherData.length) {
        return (
            <Card sx={{ 
                backgroundColor: '#181F2A', 
                borderRadius: 1.5, 
                boxShadow: '0 2px 8px 0 rgba(0,0,0,0.20)', 
                p: 3,
                mb: 2
            }}>
                <Typography variant="body1" sx={{ color: '#AAB4C8' }}>
                    Loading weather data...
                </Typography>
            </Card>
        );
    }

    return (
        <Box sx={{
            width: '100%',
            height: 'calc(100vh - 80px)',
            minHeight: 0,
            p: 0,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
            bgcolor: '#181F2A',
            overflow: 'hidden',
        }}>
            {/* Mobile Layout - Stacked */}
            {isMobile ? (
                <Box sx={{ 
                    flex: 1,
                    minHeight: 0,
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 2,
                    bgcolor: '#181F2A',
                    p: 3,
                    overflow: 'auto',
                }}>
                    {/* Crop Details Card */}
                    <Card sx={{ 
                        width: '100%', 
                        minHeight: 'fit-content',
                        backgroundColor: '#181F2A', 
                        color: '#fff', 
                        borderRadius: 1.5, 
                        boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)', 
                        p: 3, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        justifyContent: 'flex-start', 
                        overflow: 'hidden' 
                    }}>
                        <FarmDetailsCard crop={cropDetails} onBack={onBack} />
                    </Card>
                    
                    {/* Crop Activity History */}
                    <Card sx={{ 
                        width: '100%', 
                        minHeight: 400,
                        backgroundColor: '#181F2A', 
                        color: '#fff', 
                        borderRadius: 1.5, 
                        boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)', 
                        p: 3, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        justifyContent: 'flex-start', 
                        overflow: 'hidden' 
                    }}>
                        <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 500, fontSize: 15, mb: 2 }}>
                            Crop Activity History
                        </Typography>
                        <MobileTimelineEvents events={events} startDate={startDate} endDate={endDate} units_planted={units_planted} />
                    </Card>
                    
                    {/* Event Usage Summary */}
                    <Card sx={{ 
                        width: '100%', 
                        minHeight: 350,
                        backgroundColor: '#181F2A', 
                        color: '#fff', 
                        borderRadius: 1.5, 
                        boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)', 
                        p: 3, 
                        pb: 4,
                        display: 'flex', 
                        flexDirection: 'column', 
                        justifyContent: 'flex-start', 
                        overflow: 'hidden' 
                    }}>
                        <EventUsageSummary events={events} unitsPlanted={units_planted || 1} />
                    </Card>
                    
                    {/* Temperature Chart */}
                    <Card sx={{ 
                        width: '100%', 
                        minHeight: 560,
                        backgroundColor: '#181F2A', 
                        color: '#fff', 
                        borderRadius: 1.5, 
                        boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)', 
                        p: 3, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        justifyContent: 'flex-start', 
                        overflow: 'hidden' 
                    }}>
                        {weatherError === 'Daily request limit reached.' ? (
                            <ChartFallback message={weatherError} />
                        ) : (
                            <>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 2, gap: 2, pl: 1 }}>
                                    {/* Avg Max Temp Stat */}
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: '#AAB4C8', fontWeight: 500, fontSize: 15, mb: 0.5 }}>
                                            Avg Max Temp
                                        </Typography>
                                        <Typography variant="h3" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.1, fontSize: 28 }}>
                                            {avgMaxTemp ? avgMaxTemp.toFixed(1) : '--'}°C
                                        </Typography>
                                        <Box sx={{ mt: 1, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{
                                                bgcolor: maxTempIsIncrease ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)',
                                                color: maxTempIsIncrease ? '#4caf50' : '#f44336',
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: 2,
                                                fontWeight: 600,
                                                fontSize: 16,
                                                display: 'flex',
                                                alignItems: 'center',
                                                minWidth: 60,
                                                justifyContent: 'center',
                                            }}>
                                                {maxTempIsIncrease ? <ArrowUpwardIcon sx={{ fontSize: 18, mr: 0.5 }} /> : <ArrowDownwardIcon sx={{ fontSize: 18, mr: 0.5 }} />}
                                                {Math.abs(maxTempPercentChange).toFixed(1)}%
                                            </Box>
                                            {prevStart && prevEnd && (
                                                <Typography variant="caption" sx={{ color: '#AAB4C8', ml: 1 }}>
                                                    vs. {prevStart.toISOString().split('T')[0]} to {prevEnd.toISOString().split('T')[0]}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                    {/* Avg Min Temp Stat */}
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: '#AAB4C8', fontWeight: 500, fontSize: 15, mb: 0.5 }}>
                                            Avg Min Temp
                                        </Typography>
                                        <Typography variant="h3" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.1, fontSize: 28 }}>
                                            {avgMinTemp ? avgMinTemp.toFixed(1) : '--'}°C
                                        </Typography>
                                        <Box sx={{ mt: 1, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{
                                                bgcolor: minTempIsIncrease ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)',
                                                color: minTempIsIncrease ? '#4caf50' : '#f44336',
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: 2,
                                                fontWeight: 600,
                                                fontSize: 16,
                                                display: 'flex',
                                                alignItems: 'center',
                                                minWidth: 60,
                                                justifyContent: 'center',
                                            }}>
                                                {minTempIsIncrease ? <ArrowUpwardIcon sx={{ fontSize: 18, mr: 0.5 }} /> : <ArrowDownwardIcon sx={{ fontSize: 18, mr: 0.5 }} />}
                                                {Math.abs(minTempPercentChange).toFixed(1)}%
                                            </Box>
                                            {prevStart && prevEnd && (
                                                <Typography variant="caption" sx={{ color: '#AAB4C8', ml: 1 }}>
                                                    vs. {prevStart.toISOString().split('T')[0]} to {prevEnd.toISOString().split('T')[0]}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                                <Box sx={{ flex: 1, minHeight: 300, width: '100%', pl: 0, pr: 1, pb: 1 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={weatherData} syncId="cropTimeline" margin={{ top: 5, right: 5, bottom: 20, left: -20 }}>
                                            <XAxis 
                                                dataKey="date" 
                                                tick={{ fill: '#AAB4C8', fontSize: 12 }}
                                                axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                                tickLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                            />
                                            <YAxis 
                                                tick={{ fill: '#AAB4C8', fontSize: 12 }}
                                                axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                                tickLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                                padding={{ left: 0 }}
                                            />
                                            <Tooltip content={<WeatherTooltip />} />
                                            {/* Banded area between min and max temp */}
                                            <defs>
                                                <linearGradient id="tempBand" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="#F9AA33" stopOpacity={0.25} />
                                                    <stop offset="100%" stopColor="#2196f3" stopOpacity={0.10} />
                                                </linearGradient>
                                            </defs>
                                            <Area type="monotone" dataKey="temp_max" stroke={null} fill="url(#tempBand)" fillOpacity={1}
                                                isAnimationActive={false}
                                                activeDot={false}
                                                dot={false}
                                                baseLine={d => d.temp_min}
                                            />
                                            <Line dataKey="temp_min" stroke="#82ca9d" dot={false} strokeWidth={2} />
                                            <Line dataKey="temp_max" stroke="#ff7300" dot={false} strokeWidth={2} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </Box>
                            </>
                        )}
                    </Card>
                    
                    {/* Precipitation Chart */}
                    <Card sx={{ 
                        width: '100%', 
                        minHeight: 560,
                        backgroundColor: '#181F2A', 
                        color: '#fff', 
                        borderRadius: 1.5, 
                        boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)', 
                        p: 3, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        justifyContent: 'flex-start', 
                        overflow: 'hidden' 
                    }}>
                        {weatherError === 'Daily request limit reached.' ? (
                            <ChartFallback message={weatherError} />
                        ) : (
                            <>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 2, gap: 2, pl: 1 }}>
                                    {/* Precipitation Stat */}
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: '#AAB4C8', fontWeight: 500, fontSize: 15, mb: 0.5 }}>
                                            Total Precipitation
                                        </Typography>
                                        <Typography variant="h3" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.1, fontSize: 28 }}>
                                            {totalPrecip.toFixed(1)} mm
                                        </Typography>
                                        <Box sx={{ mt: 1, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{
                                                bgcolor: isIncrease ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)',
                                                color: isIncrease ? '#4caf50' : '#f44336',
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: 2,
                                                fontWeight: 600,
                                                fontSize: 16,
                                                display: 'flex',
                                                alignItems: 'center',
                                                minWidth: 60,
                                                justifyContent: 'center',
                                            }}>
                                                {isIncrease ? <ArrowUpwardIcon sx={{ fontSize: 18, mr: 0.5 }} /> : <ArrowDownwardIcon sx={{ fontSize: 18, mr: 0.5 }} />}
                                                {Math.abs(percentChange).toFixed(1)}%
                                            </Box>
                                            {prevStart && prevEnd && (
                                                <Typography variant="caption" sx={{ color: '#AAB4C8', ml: 1 }}>
                                                    vs. {prevStart.toISOString().split('T')[0]} to {prevEnd.toISOString().split('T')[0]}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                    {/* ET Stat */}
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: '#AAB4C8', fontWeight: 500, fontSize: 15, mb: 0.5 }}>
                                            Total Evapotranspiration
                                        </Typography>
                                        <Typography variant="h3" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.1, fontSize: 28 }}>
                                            {totalET.toFixed(1)} mm
                                        </Typography>
                                        <Box sx={{ mt: 1, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{
                                                bgcolor: etIsIncrease ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)',
                                                color: etIsIncrease ? '#4caf50' : '#f44336',
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: 2,
                                                fontWeight: 600,
                                                fontSize: 16,
                                                display: 'flex',
                                                alignItems: 'center',
                                                minWidth: 60,
                                                justifyContent: 'center',
                                            }}>
                                                {etIsIncrease ? <ArrowUpwardIcon sx={{ fontSize: 18, mr: 0.5 }} /> : <ArrowDownwardIcon sx={{ fontSize: 18, mr: 0.5 }} />}
                                                {Math.abs(etPercentChange).toFixed(1)}%
                                            </Box>
                                            {prevStart && prevEnd && (
                                                <Typography variant="caption" sx={{ color: '#AAB4C8', ml: 1 }}>
                                                    vs. {prevStart.toISOString().split('T')[0]} to {prevEnd.toISOString().split('T')[0]}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                                <Box sx={{ flex: 1, minHeight: 300, width: '100%', pl: 0, pr: 1, pb: 1 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <ComposedChart data={weatherData} syncId="cropTimeline" margin={{ top: 5, right: 5, bottom: 20, left: -20 }} barCategoryGap={0.5} barGap={2}>
                                            <XAxis 
                                                dataKey="date" 
                                                tick={{ fill: '#AAB4C8', fontSize: 12 }}
                                                axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                                tickLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                            />
                                            <YAxis 
                                                tick={{ fill: '#AAB4C8', fontSize: 12 }}
                                                axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                                tickLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                                padding={{ left: 0 }}
                                            />
                                            <Tooltip content={<WeatherTooltip />} />
                                            <Bar dataKey="precipitation" fill={SECONDARY} radius={[8,8,0,0]} barSize={24} />
                                            <Line type="monotone" dataKey="et" stroke="#4caf50" strokeWidth={3} dot={false} name="Reference ET" />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </Box>
                            </>
                        )}
                    </Card>
                </Box>
            ) : (
                // Desktop Layout - Original side-by-side
                <>
                    {/* Top Section - 60% height */}
                    <Box sx={{ 
                        flex: '0 0 55%',
                        minHeight: 0,
                        display: 'flex', 
                        gap: 2,
                        bgcolor: '#181F2A',
                        p: 2,
                    }}>
                        {/* Left Farm Details Card - 25% width */}
                        <Box sx={{ width: '25%', height: '100%', minHeight: 0 }}>
                            <FarmDetailsCard crop={cropDetails} onBack={onBack} />
                        </Box>
                        
                        {/* Center Crop Activity History - 50% width */}
                        <Card sx={{ 
                            width: '55%', 
                            height: '100%',
                            minHeight: 0,
                            backgroundColor: '#181F2A', 
                            color: '#fff', 
                            borderRadius: 1.5, 
                            boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)', 
                            p: 3, 
                            display: 'flex', 
                            flexDirection: 'column', 
                            justifyContent: 'flex-start', 
                            overflow: 'hidden' 
                        }}>
                            <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 500, fontSize: 15, mb: 2 }}>
                                Crop Activity History
                            </Typography>
                            <Box sx={{ flex: 1, minHeight: 0, height: '100%', overflow: 'auto' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={eventData}>
                                        <XAxis 
                                            dataKey="date" 
                                            domain={[startDate, endDate]}
                                            tick={{ fill: '#AAB4C8', fontSize: 12 }}
                                            axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                            tickLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                        />
                                        <YAxis
                                            type="category"
                                            dataKey="category"
                                            domain={['sale', 'harvest', 'insecticide', 'herbicide', 'fungicide', 'fertilizer']}
                                            tick={{ fill: '#AAB4C8', fontSize: 10 }}
                                            axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                            tickLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                        />
                                        <Tooltip content={<EventTooltip />} />
                                        {Object.keys(eventTypeColors).map((type) => (
                                            <Line
                                                key={type}
                                                dataKey={(entry) => (entry.category === type ? entry.category : null)}
                                                stroke="none"
                                                dot={{ r: 5, fill: eventTypeColors[type] }}
                                                name={type.charAt(0).toUpperCase() + type.slice(1)}
                                            />
                                        ))}
                                    </LineChart>
                                </ResponsiveContainer>
                            </Box>
                        </Card>
                        
                        {/* Right Usage Summary - 25% width */}
                        <Card sx={{ 
                            width: '20%', 
                            height: '100%',
                            minHeight: 0,
                            backgroundColor: '#181F2A', 
                            color: '#fff', 
                            borderRadius: 1.5, 
                            boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)', 
                            p: 3, 
                            display: 'flex', 
                            flexDirection: 'column', 
                            justifyContent: 'flex-start', 
                            overflow: 'hidden' 
                        }}>
                            <EventUsageSummary events={events} unitsPlanted={units_planted || 1} />
                        </Card>
                    </Box>
                    
                    {/* Bottom Section - 40% height */}
                    <Box sx={{ 
                        flex: '1 1 45%',
                        minHeight: 0,
                        display: 'flex', 
                        gap: 2,
                        bgcolor: '#181F2A',
                        overflow: 'hidden',
                        p: 2,
                    }}>
                        {/* Precipitation chart - 50% width */}
                        <Card sx={{ 
                            width: '50%', 
                            height: '100%',
                            minHeight: 0,
                            backgroundColor: '#181F2A', 
                            color: '#fff', 
                            borderRadius: 1.5, 
                            boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)', 
                            p: 3, 
                            display: 'flex', 
                            flexDirection: 'column', 
                            justifyContent: 'flex-start', 
                            overflow: 'hidden',
                            flex: 1,
                        }}>
                            {weatherError === 'Daily request limit reached.' ? (
                                <ChartFallback message={weatherError} />
                            ) : (
                                <>
                                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', mb: 1, gap: 4 }}>
                                        {/* Precipitation Stat */}
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ color: '#AAB4C8', fontWeight: 500, fontSize: 15, mb: 0.5 }}>
                                                Total Precipitation
                                            </Typography>
                                            <Typography variant="h3" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.1, fontSize: 28 }}>
                                                {totalPrecip.toFixed(1)} mm
                                            </Typography>
                                            <Box sx={{ mt: 1, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{
                                                    bgcolor: isIncrease ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)',
                                                    color: isIncrease ? '#4caf50' : '#f44336',
                                                    px: 1.5,
                                                    py: 0.5,
                                                    borderRadius: 2,
                                                    fontWeight: 600,
                                                    fontSize: 16,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    minWidth: 60,
                                                    justifyContent: 'center',
                                                }}>
                                                    {isIncrease ? <ArrowUpwardIcon sx={{ fontSize: 18, mr: 0.5 }} /> : <ArrowDownwardIcon sx={{ fontSize: 18, mr: 0.5 }} />}
                                                    {Math.abs(percentChange).toFixed(1)}%
                                                </Box>
                                                {prevStart && prevEnd && (
                                                    <Typography variant="caption" sx={{ color: '#AAB4C8', ml: 1 }}>
                                                        vs. {prevStart.toISOString().split('T')[0]} to {prevEnd.toISOString().split('T')[0]}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        {/* ET Stat */}
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ color: '#AAB4C8', fontWeight: 500, fontSize: 15, mb: 0.5 }}>
                                                Total Evapotranspiration
                                            </Typography>
                                            <Typography variant="h3" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.1, fontSize: 28 }}>
                                                {totalET.toFixed(1)} mm
                                            </Typography>
                                            <Box sx={{ mt: 1, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{
                                                    bgcolor: etIsIncrease ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)',
                                                    color: etIsIncrease ? '#4caf50' : '#f44336',
                                                    px: 1.5,
                                                    py: 0.5,
                                                    borderRadius: 2,
                                                    fontWeight: 600,
                                                    fontSize: 16,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    minWidth: 60,
                                                    justifyContent: 'center',
                                                }}>
                                                    {etIsIncrease ? <ArrowUpwardIcon sx={{ fontSize: 18, mr: 0.5 }} /> : <ArrowDownwardIcon sx={{ fontSize: 18, mr: 0.5 }} />}
                                                    {Math.abs(etPercentChange).toFixed(1)}%
                                                </Box>
                                                {prevStart && prevEnd && (
                                                    <Typography variant="caption" sx={{ color: '#AAB4C8', ml: 1 }}>
                                                        vs. {prevStart.toISOString().split('T')[0]} to {prevEnd.toISOString().split('T')[0]}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <ComposedChart data={weatherData} syncId="cropTimeline" margin={{ top: 5, right: 5, bottom: 20, left: -20 }} barCategoryGap={0.5} barGap={2}>
                                                <XAxis 
                                                    dataKey="date" 
                                                    tick={{ fill: '#AAB4C8', fontSize: 12 }}
                                                    axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                                    tickLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                                />
                                                <YAxis 
                                                    tick={{ fill: '#AAB4C8', fontSize: 12 }}
                                                    axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                                    tickLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                                    padding={{ left: 0 }}
                                                />
                                                <Tooltip content={<WeatherTooltip />} />
                                                <Bar dataKey="precipitation" fill={SECONDARY} radius={[8,8,0,0]} barSize={24} />
                                                <Line type="monotone" dataKey="et" stroke="#4caf50" strokeWidth={3} dot={false} name="Reference ET" />
                                            </ComposedChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </>
                            )}
                        </Card>
                        
                        {/* Temperature chart - 50% width */}
                        <Card sx={{ 
                            width: '50%', 
                            height: '100%',
                            minHeight: 0,
                            backgroundColor: '#181F2A', 
                            color: '#fff', 
                            borderRadius: 1.5, 
                            boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)', 
                            p: 3, 
                            display: 'flex', 
                            flexDirection: 'column', 
                            justifyContent: 'flex-start', 
                            overflow: 'hidden',
                            flex: 1,
                        }}>
                            {weatherError === 'Daily request limit reached.' ? (
                                <ChartFallback message={weatherError} />
                            ) : (
                                <>
                                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', mb: 1, gap: 4 }}>
                                        {/* Avg Max Temp Stat */}
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ color: '#AAB4C8', fontWeight: 500, fontSize: 15, mb: 0.5 }}>
                                                Avg Max Temp
                                            </Typography>
                                            <Typography variant="h3" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.1, fontSize: 28 }}>
                                                {avgMaxTemp ? avgMaxTemp.toFixed(1) : '--'}°C
                                            </Typography>
                                            <Box sx={{ mt: 1, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{
                                                    bgcolor: maxTempIsIncrease ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)',
                                                    color: maxTempIsIncrease ? '#4caf50' : '#f44336',
                                                    px: 1.5,
                                                    py: 0.5,
                                                    borderRadius: 2,
                                                    fontWeight: 600,
                                                    fontSize: 16,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    minWidth: 60,
                                                    justifyContent: 'center',
                                                }}>
                                                    {maxTempIsIncrease ? <ArrowUpwardIcon sx={{ fontSize: 18, mr: 0.5 }} /> : <ArrowDownwardIcon sx={{ fontSize: 18, mr: 0.5 }} />}
                                                    {Math.abs(maxTempPercentChange).toFixed(1)}%
                                                </Box>
                                                {prevStart && prevEnd && (
                                                    <Typography variant="caption" sx={{ color: '#AAB4C8', ml: 1 }}>
                                                        vs. {prevStart.toISOString().split('T')[0]} to {prevEnd.toISOString().split('T')[0]}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        {/* Avg Min Temp Stat */}
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ color: '#AAB4C8', fontWeight: 500, fontSize: 15, mb: 0.5 }}>
                                                Avg Min Temp
                                            </Typography>
                                            <Typography variant="h3" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.1, fontSize: 28 }}>
                                                {avgMinTemp ? avgMinTemp.toFixed(1) : '--'}°C
                                            </Typography>
                                            <Box sx={{ mt: 1, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{
                                                    bgcolor: minTempIsIncrease ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)',
                                                    color: minTempIsIncrease ? '#4caf50' : '#f44336',
                                                    px: 1.5,
                                                    py: 0.5,
                                                    borderRadius: 2,
                                                    fontWeight: 600,
                                                    fontSize: 16,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    minWidth: 60,
                                                    justifyContent: 'center',
                                                }}>
                                                    {minTempIsIncrease ? <ArrowUpwardIcon sx={{ fontSize: 18, mr: 0.5 }} /> : <ArrowDownwardIcon sx={{ fontSize: 18, mr: 0.5 }} />}
                                                    {Math.abs(minTempPercentChange).toFixed(1)}%
                                                </Box>
                                                {prevStart && prevEnd && (
                                                    <Typography variant="caption" sx={{ color: '#AAB4C8', ml: 1 }}>
                                                        vs. {prevStart.toISOString().split('T')[0]} to {prevEnd.toISOString().split('T')[0]}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={weatherData} syncId="cropTimeline" margin={{ top: 5, right: 5, bottom: 20, left: -20 }}>
                                                <XAxis 
                                                    dataKey="date" 
                                                    tick={{ fill: '#AAB4C8', fontSize: 12 }}
                                                    axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                                    tickLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                                />
                                                <YAxis 
                                                    tick={{ fill: '#AAB4C8', fontSize: 12 }}
                                                    axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                                    tickLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                                    padding={{ left: 0 }}
                                                />
                                                <Tooltip content={<WeatherTooltip />} />
                                                {/* Banded area between min and max temp */}
                                                <defs>
                                                    <linearGradient id="tempBand" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="0%" stopColor="#F9AA33" stopOpacity={0.25} />
                                                        <stop offset="100%" stopColor="#2196f3" stopOpacity={0.10} />
                                                    </linearGradient>
                                                </defs>
                                                <Area type="monotone" dataKey="temp_max" stroke={null} fill="url(#tempBand)" fillOpacity={1}
                                                    isAnimationActive={false}
                                                    activeDot={false}
                                                    dot={false}
                                                    baseLine={d => d.temp_min}
                                                />
                                                <Line dataKey="temp_min" stroke="#82ca9d" dot={false} strokeWidth={2} />
                                                <Line dataKey="temp_max" stroke="#ff7300" dot={false} strokeWidth={2} />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </>
                            )}
                        </Card>
                    </Box>
                </>
            )}
        </Box>
    );
};

export default CropTimeline;